import api from './api';
import { UserPreferences } from '../store/preferencesSlice';

// API Response interfaces
interface PreferencesApiResponse {
  success: boolean;
  data?: UserPreferences;
  message?: string;
  version?: number;
  lastUpdated?: string;
  error?: string;
}

// Preferences API service
class PreferencesApiService {

  /**
   * Get user preferences from backend
   */
  async getUserPreferences(): Promise<UserPreferences> {
    try {
      const response = await api.get<PreferencesApiResponse>('/api/user/preferences');

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to load preferences');
      }
    } catch (error: any) {
      // If API fails (e.g., 401 unauthorized), fall back to defaults
      if (error.response?.status === 401) {
        console.warn('User not authenticated for preferences API, using defaults');
        throw new Error('Authentication required');
      }

      // For other errors, log and throw
      console.error('Preferences API error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to load preferences');
    }
  }

  /**
   * Save user preferences to backend
   */
  async saveUserPreferences(preferences: UserPreferences): Promise<UserPreferences> {
    try {
      const response = await api.put<PreferencesApiResponse>(
        '/api/user/preferences',
        preferences,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to save preferences');
      }
    } catch (error: any) {
      console.error('Save preferences API error:', error);

      // Handle specific error cases
      if (error.response?.status === 400) {
        throw new Error('Invalid preferences data');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required');
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions');
      }

      throw new Error(error.response?.data?.error || error.message || 'Failed to save preferences');
    }
  }

  /**
   * Reset user preferences to defaults
   */
  async resetUserPreferences(): Promise<UserPreferences> {
    try {
      const response = await api.delete<PreferencesApiResponse>('/api/user/preferences');

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to reset preferences');
      }
    } catch (error: any) {
      console.error('Reset preferences API error:', error);

      if (error.response?.status === 401) {
        throw new Error('Authentication required');
      }

      throw new Error(error.response?.data?.error || error.message || 'Failed to reset preferences');
    }
  }
}

// Export singleton instance
export const preferencesApi = new PreferencesApiService();
export default preferencesApi;
